import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ContentLoader,
  DescriptionTable,
  DeviceIcon,
  IconCheckmark,
  IconDisabled,
  List,
} from 'shared/components';
import { displayValue, formatDate } from 'shared/helpers';
import { fetchAccountDevices } from '../../../actions';
import '../../styles.scss';

const PAGE_SIZE = 20;

const DevicesTab = ({ accountId }) => {
  const { t } = useTranslation();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [currentSort, setCurrentSort] = useState([]);
  const [retryToken, setRetryToken] = useState(0);
  const [tableExpanded, setTableExpanded] = useState({});

  useEffect(() => {
    setPage(0);
    setTableExpanded({});
  }, [accountId]);

  useEffect(() => {
    if (!accountId) return;
    let isStale = false;
    setLoading(true);
    setError(false);
    setTableExpanded({});
    fetchAccountDevices(accountId, {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      orderBy: currentSort,
    })
      .then((res) => {
        const payload = get(res, 'data');
        const results = Array.isArray(payload)
          ? payload
          : get(payload, 'results', []);
        const totalCount = Array.isArray(payload)
          ? results.length
          : get(payload, 'count', results.length);
        if (!isStale) {
          setDevices(results);
          setCount(totalCount);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!isStale) {
          setError(true);
          setDevices([]);
          setCount(0);
          setLoading(false);
        }
      });
  }, [accountId, currentSort, page, retryToken]);

  const handleRetry = () => setRetryToken((token) => token + 1);

  const renderBooleanIcon = (value) =>
    value ? (
      <IconCheckmark color="#10ac84" height="14px" />
    ) : (
      <IconDisabled color="#aaaaaa" fill="#aaaaaa" height="14px" />
    );

  const columns = [
    {
      expander: true,
      Header: t('Details'),
      headerClassName: 'text-center',
      width: 80,
      style: {
        fontSize: 25,
        padding: '0',
        textAlign: 'center',
        userSelect: 'none',
      },
    },
    {
      Header: t('Type'),
      id: 'device_type',
      className: 'text-center',
      headerClassName: 'text-center',
      width: 70,
      accessor: (row) => get(row, 'type') ?? get(row, 'device.type') ?? null,
      Cell: (cellInfo) => (
        <div className="device-type-icon">
          <DeviceIcon device={cellInfo.value} />
        </div>
      ),
    },
    {
      Header: t('Hardware ID'),
      id: 'hardware_id',
      accessor: (row) =>
        get(row, 'hardware_id') ?? get(row, 'device.hardware_id') ?? null,
      Cell: (cellData) => displayValue(cellData.value),
    },
    {
      Header: t('Last Check'),
      id: 'last_check',
      headerClassName: 'text-center',
      className: 'text-center',
      accessor: (row) =>
        get(row, 'last_check') ?? get(row, 'device.last_check') ?? null,
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 140,
    },
    {
      Header: t('Status'),
      id: 'device_active',
      headerClassName: 'text-center',
      className: 'text-center',
      accessor: (row) =>
        get(row, 'device_active') ?? get(row, 'device.device_active') ?? null,
      Cell: (cellData) => renderBooleanIcon(cellData.value),
      width: 90,
    },
    {
      Header: t('Device Variables'),
      id: 'device_variables',
      accessor: (row) =>
        get(row, 'device_variables') ?? get(row, 'device.device_variables'),
      Cell: (cellData) => {
        const value = cellData.value;
        if (!value || Object.keys(value).length === 0) {
          return displayValue(null, '{}');
        }
        return displayValue(JSON.stringify(value));
      },
      maxWidth: 260,
    },
    {
      Header: t('Floating in use'),
      id: 'floating_in_use',
      headerClassName: 'text-center',
      className: 'text-center',
      accessor: (row) =>
        get(row, 'floating_in_use') ??
        get(row, 'device.floating_in_use') ??
        null,
      Cell: (cellData) => renderBooleanIcon(cellData.value),
      width: 120,
    },
    {
      Header: t('Borrowed'),
      id: 'borrowed',
      headerClassName: 'text-center',
      className: 'text-center',
      accessor: (row) =>
        Boolean(
          get(row, 'borrowed_until') ?? get(row, 'device.borrowed_until'),
        ),
      Cell: (cellData) => renderBooleanIcon(cellData.value),
      width: 100,
    },
    {
      Header: t('Floating last check'),
      id: 'floating_last_seen',
      headerClassName: 'text-center',
      className: 'text-center',
      accessor: (row) =>
        get(row, 'floating_last_seen') ??
        get(row, 'device.floating_last_seen') ??
        null,
      Cell: (cellData) => {
        const value = cellData.value;
        const isDateLikeValue =
          value instanceof Date ||
          typeof value === 'string' ||
          typeof value === 'number';

        if (isDateLikeValue) {
          return formatDate(value);
        }

        return renderBooleanIcon(false);
      },
      width: 160,
    },
  ];

  if (loading) {
    return <ContentLoader text={t('Loading devices')} />;
  }

  if (error) {
    return (
      <div className="CustomerAccount-error">
        <p>{t('Unable to load devices for this account')}</p>
        <Button theme="info" size="sm" onClick={handleRetry}>
          {t('Retry')}
        </Button>
      </div>
    );
  }

  if (!devices.length) {
    return (
      <div className="CustomerAccount-empty">
        {t('No devices for this account')}
      </div>
    );
  }

  const pages = Math.ceil(count / PAGE_SIZE);

  return (
    <div className="CustomerAccount-list">
      <List
        onExpandedChange={(expanded) => setTableExpanded(expanded)}
        expanded={tableExpanded}
        SubComponent={(row) => (
          <div className="SubComponent">
            <DescriptionTable
              details={[
                {
                  label: t('First activated'),
                  value: formatDate(
                    get(row, 'original.time_activated') ??
                      get(row, 'original.device.time_activated'),
                  ),
                },
                {
                  label: t('OS'),
                  value: displayValue(
                    get(row, 'original.os') ?? get(row, 'original.device.os'),
                  ),
                },
                {
                  label: t('SDK build'),
                  value: displayValue(
                    get(row, 'original.sdk_build_version') ??
                      get(row, 'original.device.sdk_build_version'),
                  ),
                },
                {
                  label: t('App version'),
                  value: displayValue(
                    get(row, 'original.app_ver') ??
                      get(row, 'original.device.app_ver'),
                  ),
                },
                {
                  label: t('Hostname'),
                  value: displayValue(
                    get(row, 'original.hostname') ??
                      get(row, 'original.device.hostname'),
                  ),
                },
                {
                  label: t('External IP address'),
                  value: displayValue(
                    get(row, 'original.external_ip') ??
                      get(row, 'original.device.external_ip'),
                  ),
                },
                {
                  label: t('IP address'),
                  value: displayValue(
                    get(row, 'original.ip') ?? get(row, 'original.device.ip'),
                  ),
                },
                {
                  label: t('MAC address'),
                  value: displayValue(
                    get(row, 'original.mac_address') ??
                      get(row, 'original.device.mac_address'),
                  ),
                },
                {
                  label: t('Is virtual machine'),
                  value:
                    (get(row, 'original.is_vm') ??
                    get(row, 'original.device.is_vm'))
                      ? t('Yes')
                      : t('No'),
                },
                {
                  label: t('VM info'),
                  value: displayValue(
                    get(row, 'original.vm_info') ??
                      get(row, 'original.device.vm_info'),
                  ),
                },
              ]}
            />
          </div>
        )}
        data={devices}
        columns={columns}
        page={page}
        pages={pages}
        manual
        minRows={devices.length || 5}
        showPagination={pages > 1}
        loading={loading}
        onPageChange={(newPage) => setPage(newPage)}
        onSortedChange={(newSorted) => setCurrentSort(newSorted)}
        defaultSorted={currentSort}
        defaultPageSize={PAGE_SIZE}
      />
    </div>
  );
};

DevicesTab.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default DevicesTab;
